import React, { useEffect, useState } from 'react';
import './OutOfStockCard.css';
import ChocolateMs from '../../../assets/images/png/ChocolateMs.png';
import FaireverMashroom from '../../../assets/images/png/fairever-mashroom.png';
import MeeraShampoo from '../../../assets/images/png/meera.png';
import DummyProductImage from "../../../assets/image/png/dummyProduct.png";
import { getEnrichedOutOfStockDataAlternative } from '../homePageService';
import Shimmer from '../../common/Shimmer';
import { useNavigate } from 'react-router-dom';
import {truncateToTwoDecimals} from '../../dms_common/DmsUtils';

interface OutOfStockItem {
  skucode: string;
  outOfStockTime: string;
  outOfStockDays: number;
  avgDailyOrder: number;
  blobKey: string;
  skuName: string;
  packPtr: number;
  salesLost?: number;
  displayName?: string;
  [key: string]: any; 
}

interface OutOfStockCardProps {
  isCreatePrimaryOrderAvailable?: boolean;
}

const OutOfStockCard = ({ isCreatePrimaryOrderAvailable = false }: OutOfStockCardProps) => {
  const navigate = useNavigate();

  const [outOfStockItemsV2, setOutOfStockItemsV2] = useState<OutOfStockItem[]>([]);
  const [topThreeItems, setTopThreeItems] = useState<OutOfStockItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");

  const formatSkuDisplay = (value: string): string => {
    if (value.length < 20) {
      return value;
    }
    return value.substring(0, 17) + '...';
  };

  const calculateSalesLost = (avgDailyOrder: number, packPtr: number, outOfStockDays: number): number => {
    return (avgDailyOrder * packPtr) * outOfStockDays;
  };

  const formatCurrency = (amount: number): string => {
    if (amount === 0) return `${ConfigDictionary?.currency}0`;
    if (amount >= 10000000) { // 1 crore
      return `${ConfigDictionary?.currency}${truncateToTwoDecimals(amount / 10000000)}Cr`;
    } else if (amount >= 100000) { // 1 lakh
      return `${ConfigDictionary?.currency}${truncateToTwoDecimals(amount / 100000)}L`;
    } else if (amount >= 1000) { // 1 thousand
      return `${ConfigDictionary?.currency}${truncateToTwoDecimals(amount / 1000)}K`;
    } else {
      return `${ConfigDictionary?.currency}${truncateToTwoDecimals(amount)}`;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getEnrichedOutOfStockDataAlternative("");
        
        // Process and enrich the data
        const enrichedData = data.map((item: OutOfStockItem) => ({
          ...item,
          salesLost: calculateSalesLost(item.avgDailyOrder, item.packPtr, item.outOfStockDays),
        }));

        // Sort by avgDailyOrder in descending order and take top 3
        const sortedData = enrichedData
          .filter(item => item.avgDailyOrder >= 0) // Only include items with orders
          .sort((a, b) => b.avgDailyOrder - a.avgDailyOrder)
          .slice(0, 3);
        setOutOfStockItemsV2(enrichedData);
        setTopThreeItems(sortedData);
      } catch (error) {
        console.error('Error fetching out of stock data:', error);
        setError('Error fetching out of stock data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const roundOffValue = (value: number) => {
    if (value > 0 && value < 1) {
      return 1;
    }
    return Math.round(value);
  }

  const handleReorder = () => {
    navigate('/dashboard/purchase/createPrimaryOrder', {
      state: {
        reStock: true,
        skuCodes: outOfStockItemsV2.map(item => item.skucode),
      },
    });
  };
  

  return (
    <div className={`out-of-stock-card ${isLoading ? 'shimmer-card loading-state' : ''}`}>
      {isLoading && <div className="loading-overlay" />}
      <div className="out-of-stock-header">
        <div className="out-of-stock-title">
          <div className="out-of-stock-icon">
            {isLoading ? (
              <Shimmer width="18px" height="18px" borderRadius="50%" className="shimmer-icon" />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', color: '#D55151' }}>
                <img src={require('../../../assets/images/png/3. Danger.svg').default} alt="Danger" style={{ width: 18, height: 18 }} />
              </span>
            )}
          </div>
          <span className="out-of-stock-count">
            {isLoading ? (
              <Shimmer width="200px" height="20px" />
            ) : (
              <>
                <span style={{ color: '#000000', fontWeight: 700 }}>{outOfStockItemsV2.length}</span> SKUs are out-of-stock
              </>
            )}
          </span>
        </div>
        {isLoading ? (
          <Shimmer width="100px" height="36px" className="shimmer-button" />
        ) : outOfStockItemsV2.length > 0 && isCreatePrimaryOrderAvailable ? (
          <button className="out-of-stock-btn" onClick={handleReorder}>Reorder</button>
        ) : null}
      </div>

      <div className="out-of-stock-items">
        {isLoading ? (
          <>
            {[1, 2, 3].map((item) => (
              <div key={`shimmer-${item}`} className={`out-of-stock-item shimmer-item-${item}`}>
                <div className="item-info">
                  <Shimmer width="28px" height="28px" borderRadius="4px" className="shimmer-image" />
                  <div className="item-details">
                    <Shimmer 
                      width={item === 1 ? "140px" : item === 2 ? "120px" : "130px"} 
                      height="16px" 
                      className="shimmer-text shimmer-item-name" 
                    />
                    <Shimmer 
                      width={item === 1 ? "90px" : item === 2 ? "85px" : "95px"} 
                      height="14px" 
                      className="shimmer-text shimmer-item-sku" 
                      style={{ marginTop: '2px' }}
                    />
                  </div>
                </div>
                
                <div className="item-metrics">
                  <div className="item-aov">
                    <Shimmer width="75px" height="12px" className="shimmer-text shimmer-label" />
                    <Shimmer 
                      width="50px" 
                      height="20px" 
                      borderRadius="4px" 
                      className="shimmer-text shimmer-value" 
                      style={{ marginTop: '2px' }}
                    />
                  </div>
                  
                  <div className="item-date">
                    <Shimmer width="65px" height="12px" className="shimmer-text shimmer-label" />
                    <Shimmer 
                      width={item === 1 ? "70px" : item === 2 ? "75px" : "65px"} 
                      height="20px" 
                      borderRadius="4px" 
                      className="shimmer-text shimmer-value" 
                      style={{ marginTop: '2px' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : error ? (
          <div className="out-of-stock-error">
            <p>Unable to load out-of-stock data</p>
          </div>
        ) : outOfStockItemsV2.length > 0 ? (
          topThreeItems.map((item, index) => (
            <div key={`${item.skucode}-${index}`} className="out-of-stock-item">
              <div className="item-info">
                <img src={item.blobKey || ConfigDictionary?.defaultCatalogueImage || DummyProductImage} alt={item.skuName} className="item-image" onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null; 
                  target.src = DummyProductImage; 
                }} />
                <div className="item-details">
                  <h4 className="item-name" title={item.skuName}>
                    {formatSkuDisplay(item.skuName)}
                  </h4>
                  <p className="item-sku" title={item.skucode}>{formatSkuDisplay(item.skucode)}</p>
                </div>
              </div>
              
              <div className="item-metrics">
                <div className="item-aov">
                  <span className="aov-label">Per day avg L3M</span>
                  <span className="aov-value">{roundOffValue(item.avgDailyOrder)} {ConfigDictionary?.packUnitLabel}</span>
                </div>
                
                <div className="item-date">
                  <span className="date-label">Sales Loss</span>
                  <span className="date-value">{formatCurrency(item.salesLost || 0)}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="out-of-stock-error">
            <p>All SKUs are in stock</p>
          </div>
        )}
      </div>

      {outOfStockItemsV2.length > 0 && (
        <div className="out-of-stock-footer">
          {isLoading ? (
            <Shimmer width="100px" height="16px" className="shimmer-footer-link" />
          ) : (
            <a 
              href="#" 
              className="view-all-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard/stocks/realTimeStocks?filter=outOfStock&stockType=availableQty');
              }}
            >
              View all SKUs
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default OutOfStockCard;